/* tslint:disable */
/* eslint-disable */
/**
 * Distributor API
 * The Cashlink Distributor API enables you to distribute crypto securities and digital securities. It contains all building blocks from investor registration and KYC/AML to brokerage, clearing and settlement processes. While you are in full control of the frontends served to your investors you can use the Cashlink Studio to monitor and manage the activity on your platform.   Note: You cannot access data of investors or investments if they haven\'t been created via the API with your provided API credentials. Also, it\'s not possible to access data of investors that have registered via the Cashlink Signing Module, if you are using this as well.  ### Authentication  Every call to the API must be authenticated via OAuth client credentials. During setup, your point of contact will provide you with your client ID and client secret.   ### Idempotency  For certain POST endpoints, the API uses client-supplied idempotency keys to ensure that requests can be retried safely without performing the same operation twice. Repeated calls to those endpoints with the same idempotency key will return the cached response from the first call. Keys are expected to be set as header parameters with *X-Idempotency-Key*. The recommended approach for interacting with such endpoints is as follows:   - Create a unique, random key, such as a UUIDv4.   - Store the intent to call an endpoint along with the generated key in the client database.   - Flush to disk (e.g. commit an SQL transaction).   - Perform the call to the endpoint.   This ensures that even if the response from the API to a client gets lost, or the client experiences any sort of outage between steps 3 and 4, the client can always safely retry the same action.   If you already have objects corresponding to tasks in the API in your client-side database, you can use their internal IDs as idempotency keys!   ### Monetary values   Monetary values are represented as a combination of amount, decimals & currency. For example, ```{\'amount\': \'40000000\', \'decimals\': 3, \'currency\': \'EUR\'} means \'EUR 40,000.000\'```.   ### Handling documents  The document endpoint can be used to upload documents. Files are uploaded via HTTP multipart. The endpoint will return a document ID that can be used in other requests to refer to that document.   ### Webhooks  You can also register webhooks so that we can notify your application about events with regards to investments. To use that, please provide your point of contact at Cashlink with the following data:   - URL to be used as the target for webhooks  - OAuth client credentials or bearer authentication token so that we can authenticate at your server.  Webhooks are sent every time the status of an investment changes.  **POST <customer-provided URL>**  Webhooks are retried until the called endpoint returns a 2xx.  **Request**  {  \'investment_id\': \'3fa85f64-5717-4562-b3fc-2c963f66afa6\',  \'status\': \'ACCEPTED\',  \'changed_at\': \'2020-12-11T14:59:46.716Z\'  }  ### Concepts  The Cashlink Distributor API is based on the following concepts:  - Persons (legal or natural) that are users on your platform willing to invest are called investors.  - These investors will be able to invest into different financial products issued by different entities. In the API they are called products.  - The purchase of units of such a product takes place within your application and ends with a signed investment contract.  - Now you can create an investment, which contains the investor, the units, the signed investment contract and the accepted date of the investment by the issuer.   - At some point, parts of the investments are returned to the issuer because the outstanding amount has been paid back for example. Such a transaction is called a redemption.    
 *
 * The version of the OpenAPI document: v2
 * Contact: support@cashlink.de
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */


import * as runtime from '../runtime';
import type {
  AdminConfirmationSerialiserRequest,
  EmailConfirmationRequest,
  EmailResetRequest,
  PatchedUserRequest,
  ResetPasswordConfirmationRequest,
  ResetPasswordRequest,
  SessionLoginRequest,
  SetPasswordRequest,
  User,
  UserAfterConfirmation,
  UsersLogoutCreate200Response,
} from '../models/index';
import {
    AdminConfirmationSerialiserRequestFromJSON,
    AdminConfirmationSerialiserRequestToJSON,
    EmailConfirmationRequestFromJSON,
    EmailConfirmationRequestToJSON,
    EmailResetRequestFromJSON,
    EmailResetRequestToJSON,
    PatchedUserRequestFromJSON,
    PatchedUserRequestToJSON,
    ResetPasswordConfirmationRequestFromJSON,
    ResetPasswordConfirmationRequestToJSON,
    ResetPasswordRequestFromJSON,
    ResetPasswordRequestToJSON,
    SessionLoginRequestFromJSON,
    SessionLoginRequestToJSON,
    SetPasswordRequestFromJSON,
    SetPasswordRequestToJSON,
    UserFromJSON,
    UserToJSON,
    UserAfterConfirmationFromJSON,
    UserAfterConfirmationToJSON,
    UsersLogoutCreate200ResponseFromJSON,
    UsersLogoutCreate200ResponseToJSON,
} from '../models/index';

export interface UsersAdminUserConfirmationCreateRequest {
    adminConfirmationSerialiserRequest: AdminConfirmationSerialiserRequest;
}

export interface UsersEmailConfirmationCreateRequest {
    emailConfirmationRequest: EmailConfirmationRequest;
}

export interface UsersEmailResetCreateRequest {
    emailResetRequest: EmailResetRequest;
}

export interface UsersLoginCreateRequest {
    sessionLoginRequest: SessionLoginRequest;
}

export interface UsersMePartialUpdateRequest {
    patchedUserRequest?: PatchedUserRequest;
}

export interface UsersPasswordCreateRequest {
    setPasswordRequest: SetPasswordRequest;
}

export interface UsersPasswordResetConfirmationCreateRequest {
    resetPasswordConfirmationRequest: ResetPasswordConfirmationRequest;
}

export interface UsersPasswordResetCreateRequest {
    resetPasswordRequest: ResetPasswordRequest;
}

/**
 * 
 */
export class UsersApi extends runtime.BaseAPI {

    /**
     * Confirm the admin users e-mail address.
     */
    async usersAdminUserConfirmationCreateRaw(requestParameters: UsersAdminUserConfirmationCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<UserAfterConfirmation>> {
        if (requestParameters['adminConfirmationSerialiserRequest'] == null) {
            throw new runtime.RequiredError(
                'adminConfirmationSerialiserRequest',
                'Required parameter "adminConfirmationSerialiserRequest" was null or undefined when calling usersAdminUserConfirmationCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        const response = await this.request({
            path: `/api/users/admin-user/confirmation/`,
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: AdminConfirmationSerialiserRequestToJSON(requestParameters['adminConfirmationSerialiserRequest']),
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => UserAfterConfirmationFromJSON(jsonValue));
    }

    /**
     * Confirm the admin users e-mail address.
     */
    async usersAdminUserConfirmationCreate(requestParameters: UsersAdminUserConfirmationCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<UserAfterConfirmation> {
        const response = await this.usersAdminUserConfirmationCreateRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Confirm the users e-mail address.
     */
    async usersEmailConfirmationCreateRaw(requestParameters: UsersEmailConfirmationCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        if (requestParameters['emailConfirmationRequest'] == null) {
            throw new runtime.RequiredError(
                'emailConfirmationRequest',
                'Required parameter "emailConfirmationRequest" was null or undefined when calling usersEmailConfirmationCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        const response = await this.request({
            path: `/api/users/email/confirmation/`,
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: EmailConfirmationRequestToJSON(requestParameters['emailConfirmationRequest']),
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * Confirm the users e-mail address.
     */
    async usersEmailConfirmationCreate(requestParameters: UsersEmailConfirmationCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.usersEmailConfirmationCreateRaw(requestParameters, initOverrides);
    }

    /**
     * Resend the email confirmation mail.
     */
    async usersEmailResendCreateRaw(initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/users/email/resend/`,
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * Resend the email confirmation mail.
     */
    async usersEmailResendCreate(initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.usersEmailResendCreateRaw(initOverrides);
    }

    /**
     * Reset the email.
     */
    async usersEmailResetCreateRaw(requestParameters: UsersEmailResetCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        if (requestParameters['emailResetRequest'] == null) {
            throw new runtime.RequiredError(
                'emailResetRequest',
                'Required parameter "emailResetRequest" was null or undefined when calling usersEmailResetCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        const response = await this.request({
            path: `/api/users/email/reset/`,
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: EmailResetRequestToJSON(requestParameters['emailResetRequest']),
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * Reset the email.
     */
    async usersEmailResetCreate(requestParameters: UsersEmailResetCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.usersEmailResetCreateRaw(requestParameters, initOverrides);
    }

    /**
     * Session-based login that sets httpOnly cookies for authentication. CSRF token is set as a cookie (non-httpOnly) for the Double Submit Cookie pattern. Frontend reads it from cookie, not from response body.
     */
    async usersLoginCreateRaw(requestParameters: UsersLoginCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<User>> {
        if (requestParameters['sessionLoginRequest'] == null) {
            throw new runtime.RequiredError(
                'sessionLoginRequest',
                'Required parameter "sessionLoginRequest" was null or undefined when calling usersLoginCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        const response = await this.request({
            path: `/api/users/login/`,
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: SessionLoginRequestToJSON(requestParameters['sessionLoginRequest']),
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => UserFromJSON(jsonValue));
    }

    /**
     * Session-based login that sets httpOnly cookies for authentication. CSRF token is set as a cookie (non-httpOnly) for the Double Submit Cookie pattern. Frontend reads it from cookie, not from response body.
     */
    async usersLoginCreate(requestParameters: UsersLoginCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<User> {
        const response = await this.usersLoginCreateRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Logout view that clears the session and httpOnly cookies.
     */
    async usersLogoutCreateRaw(initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<UsersLogoutCreate200Response>> {
        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/users/logout/`,
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => UsersLogoutCreate200ResponseFromJSON(jsonValue));
    }

    /**
     * Logout view that clears the session and httpOnly cookies.
     */
    async usersLogoutCreate(initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<UsersLogoutCreate200Response> {
        const response = await this.usersLogoutCreateRaw(initOverrides);
        return await response.value();
    }

    /**
     * Get information about the logged-in user.
     */
    async usersMePartialUpdateRaw(requestParameters: UsersMePartialUpdateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<User>> {
        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        const response = await this.request({
            path: `/api/users/me/`,
            method: 'PATCH',
            headers: headerParameters,
            query: queryParameters,
            body: PatchedUserRequestToJSON(requestParameters['patchedUserRequest']),
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => UserFromJSON(jsonValue));
    }

    /**
     * Get information about the logged-in user.
     */
    async usersMePartialUpdate(requestParameters: UsersMePartialUpdateRequest = {}, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<User> {
        const response = await this.usersMePartialUpdateRaw(requestParameters, initOverrides);
        return await response.value();
    }

    /**
     * Get information about the logged-in user.
     */
    async usersMeRetrieveRaw(initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<User>> {
        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        const response = await this.request({
            path: `/api/users/me/`,
            method: 'GET',
            headers: headerParameters,
            query: queryParameters,
        }, initOverrides);

        return new runtime.JSONApiResponse(response, (jsonValue) => UserFromJSON(jsonValue));
    }

    /**
     * Get information about the logged-in user.
     */
    async usersMeRetrieve(initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<User> {
        const response = await this.usersMeRetrieveRaw(initOverrides);
        return await response.value();
    }

    /**
     * Set the password.
     */
    async usersPasswordCreateRaw(requestParameters: UsersPasswordCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        if (requestParameters['setPasswordRequest'] == null) {
            throw new runtime.RequiredError(
                'setPasswordRequest',
                'Required parameter "setPasswordRequest" was null or undefined when calling usersPasswordCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        const response = await this.request({
            path: `/api/users/password/`,
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: SetPasswordRequestToJSON(requestParameters['setPasswordRequest']),
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * Set the password.
     */
    async usersPasswordCreate(requestParameters: UsersPasswordCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.usersPasswordCreateRaw(requestParameters, initOverrides);
    }

    /**
     * Confirm password reset.
     */
    async usersPasswordResetConfirmationCreateRaw(requestParameters: UsersPasswordResetConfirmationCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        if (requestParameters['resetPasswordConfirmationRequest'] == null) {
            throw new runtime.RequiredError(
                'resetPasswordConfirmationRequest',
                'Required parameter "resetPasswordConfirmationRequest" was null or undefined when calling usersPasswordResetConfirmationCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        const response = await this.request({
            path: `/api/users/password/reset/confirmation/`,
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: ResetPasswordConfirmationRequestToJSON(requestParameters['resetPasswordConfirmationRequest']),
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * Confirm password reset.
     */
    async usersPasswordResetConfirmationCreate(requestParameters: UsersPasswordResetConfirmationCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.usersPasswordResetConfirmationCreateRaw(requestParameters, initOverrides);
    }

    /**
     * Reset the password.
     */
    async usersPasswordResetCreateRaw(requestParameters: UsersPasswordResetCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<runtime.ApiResponse<void>> {
        if (requestParameters['resetPasswordRequest'] == null) {
            throw new runtime.RequiredError(
                'resetPasswordRequest',
                'Required parameter "resetPasswordRequest" was null or undefined when calling usersPasswordResetCreate().'
            );
        }

        const queryParameters: any = {};

        const headerParameters: runtime.HTTPHeaders = {};

        headerParameters['Content-Type'] = 'application/json';

        const response = await this.request({
            path: `/api/users/password/reset/`,
            method: 'POST',
            headers: headerParameters,
            query: queryParameters,
            body: ResetPasswordRequestToJSON(requestParameters['resetPasswordRequest']),
        }, initOverrides);

        return new runtime.VoidApiResponse(response);
    }

    /**
     * Reset the password.
     */
    async usersPasswordResetCreate(requestParameters: UsersPasswordResetCreateRequest, initOverrides?: RequestInit | runtime.InitOverrideFunction): Promise<void> {
        await this.usersPasswordResetCreateRaw(requestParameters, initOverrides);
    }

}
